import React, { useState, useEffect } from 'react';
import './PracticeMode.css';

export interface PracticeSentence {
  id: number;
  text: string;
  targetPhonemes: string[];
  difficulty?: string;
  type?: string;
}

interface PracticeModeProps {
  onModeSelect: (mode: 'free' | 'guided', sentence?: PracticeSentence) => void;
}

const PracticeMode: React.FC<PracticeModeProps> = ({ onModeSelect }) => {
  const [selectedMode, setSelectedMode] = useState<'free' | 'guided' | null>(null);
  const [selectedSentence, setSelectedSentence] = useState<PracticeSentence | null>(null);
  const [showAllSentences, setShowAllSentences] = useState(false);
  const [practiceSentences, setPracticeSentences] = useState<PracticeSentence[]>([]);
  const [displayedSentences, setDisplayedSentences] = useState<PracticeSentence[]>([]);

  // Load practice sentences from JSON file
  useEffect(() => {
    fetch('/english-sentences.json')
      .then(response => response.json())
      .then(data => {
        setPracticeSentences(data);
        const shuffled = [...data].sort(() => 0.5 - Math.random());
        setDisplayedSentences(shuffled.slice(0, 5));
      })
      .catch(error => console.error('Failed to load practice sentences:', error));
  }, []);

  // Get random sentences for initial display
  const getRandomSentences = () => {
    const shuffled = [...practiceSentences].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 5);
  };

  const handleModeSelect = (mode: 'free' | 'guided') => {
    setSelectedMode(mode);
    onModeSelect(mode);
  };

  const handleSentenceSelect = (sentence: PracticeSentence) => {
    setSelectedSentence(sentence);
    onModeSelect('guided', sentence);
  };

  const handleShowMore = () => {
    setShowAllSentences(true);
    setDisplayedSentences(practiceSentences);
  };

  const handleShuffle = () => {
    setDisplayedSentences(getRandomSentences());
    setShowAllSentences(false);
  };

  if (!selectedMode) {
    return (
      <div className="practice-mode-selector">
        <h3>Choose Practice Mode</h3>
        <div className="mode-cards">
          <div className="mode-card" onClick={() => handleModeSelect('free')}>
            <div className="mode-icon">🎤</div>
            <h4>Free Speech</h4>
            <p>Record yourself speaking naturally about any topic</p>
            <div className="mode-benefits">
              <span>✓ Natural conversation analysis</span>
              <span>✓ Comprehensive phoneme coverage</span>
            </div>
          </div>
          
          <div className="mode-card" onClick={() => handleModeSelect('guided')}>
            <div className="mode-icon">📖</div>
            <h4>Guided Practice</h4>
            <p>Read specific sentences designed to test pronunciation</p>
            <div className="mode-benefits">
              <span>✓ Targeted phoneme practice</span>
              <span>✓ Compare against expected text</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (selectedMode === 'guided' && !selectedSentence) {
    return (
      <div className="sentence-selector">
        <h3>Select a Practice Sentence</h3>
        <p className="instructions">
          Choose a tongue twister to practice. Each one targets specific sounds that can be challenging.
        </p>
        
        <div className="sentence-list">
          {displayedSentences.map((sentence) => (
            <div 
              key={sentence.id} 
              className="sentence-card"
              onClick={() => handleSentenceSelect(sentence)}
            >
              <div className="sentence-text">{sentence.text}</div>
              <div className="phoneme-tags">
                <span className="tag-label">Practices:</span>
                {sentence.targetPhonemes.map((phoneme, idx) => (
                  <span key={idx} className="phoneme-tag">{phoneme}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        <div className="sentence-controls">
          {!showAllSentences ? (
            <button className="show-more-btn" onClick={handleShowMore}>
              Show All ({practiceSentences.length} sentences)
            </button>
          ) : (
            <button className="shuffle-btn" onClick={handleShuffle}>
              🔀 Show Random Selection
            </button>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default PracticeMode;